import type { WebHookValues } from '../WebHookForm';
import {
  type WebHookRowData,
  type WebHookType,
  type WebhookTrigger,
  webHookTypes,
} from '../WebHooksState';
import { DEFAULT_PAYLOAD_TEMPLATE } from '../constants';

const DEFAULT_PAYLOAD: WebHookValues['payloadTemplate'] = {
  format: 'json',
  template: DEFAULT_PAYLOAD_TEMPLATE,
};

// The `headerTemplate` map is persisted unordered; the form edits it as an
// ordered list of { key, value } rows.
const deserializeHeaders = (
  headerTemplate: Record<string, string> | null | undefined,
): WebHookValues['headers'] =>
  Object.entries(headerTemplate ?? {}).map(([key, value]) => ({ key, value }));

const INITIAL_VALUES: Omit<WebHookValues, 'types'> = {
  active: true,
  name: '',
  useTriggers: false,
  sendAll: true,
  trigger: new Set<string>(),
  url: '',
  payloadTemplate: DEFAULT_PAYLOAD,
  headers: [],
  token: '',
  clearToken: false,
  hasToken: false,
  caCertId: null,
};

// Reverse of `serializeTrigger` in saveWebHook: rebuild the TreePicker leaf keys
// (`<type>/<action>/<status>` or `<type>/<status>`) from the persisted triggers.
const deserializeTrigger = (trigger: WebhookTrigger[] | null): Set<string> =>
  new Set(
    (trigger ?? []).map(({ type, action, status }) =>
      [type, action, status].filter(Boolean).join('/'),
    ),
  );

const getInitialValuesFromRowData = ({
  active = false,
  id,
  name,
  secret,
  sendAll,
  trigger,
  payloadTemplate,
  headerTemplate,
  hasToken,
  caCertId,
  url,
}: WebHookRowData) => ({
  active,
  id,
  name,
  secret,
  useTriggers: (trigger?.length ?? 0) > 0,
  sendAll,
  trigger: deserializeTrigger(trigger),
  // Fall back to the default so the editor always has a valid starting point.
  payloadTemplate: payloadTemplate ?? DEFAULT_PAYLOAD,
  headers: deserializeHeaders(headerTemplate),
  // The token itself is never returned; start blank and only show whether one
  // is already configured.
  token: '',
  clearToken: false,
  hasToken: hasToken ?? false,
  caCertId: caCertId ?? null,
  url,
});

export const getSelectedEventTypes = (
  webhook: WebHookRowData | null,
): WebHookValues['types'] => {
  return webHookTypes.reduce(
    (acc, type) => {
      acc[type] = webhook
        ? webhook.sendAll || webhook.types.includes(type)
        : true;

      return acc;
    },
    {} as Record<WebHookType, boolean>,
  );
};

export const getInitialValues = (
  webhook: WebHookRowData | null,
): WebHookValues => {
  const initialTextData = webhook
    ? getInitialValuesFromRowData(webhook)
    : INITIAL_VALUES;

  return {
    ...initialTextData,
    types: getSelectedEventTypes(webhook),
  };
};
